import { Component, forwardRef, Input, NgModule, ViewChild } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule, ValidationErrors, Validator } from '@angular/forms';
import { map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { CKEditor4, CKEditorComponent, CKEditorModule } from 'ckeditor4-angular';
import { BaseControl } from '@uw/core/base-control';
import { merge, Subject } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '@uw-env/environment';


@Component({
  selector: 'uw-html-editor',
  standalone: false,
  template: '<ckeditor [config]="config" (ready)="onReady($event)" editorUrl="assets/ckeditor/ckeditor.js"></ckeditor>',
  styleUrls: ["html-editor.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HtmlEditorComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => HtmlEditorComponent),
      multi: true
    }
  ]
})
export class HtmlEditorComponent extends BaseControl implements Validator {
  private readonly _afterInsertHtmlSubject: Subject<any> = new Subject();
  private readonly _afterInsertHtml$ = this._afterInsertHtmlSubject.asObservable();

  @ViewChild(CKEditorComponent, { static: true }) ckEditorComponent!: CKEditorComponent;

  @Input() tagName: string | undefined = undefined;

  constructor(
    private readonly _translateService: TranslateService
  ) {
    super();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const value = this.ckEditorComponent.instance?.getData();

    if (!value || !this.tagName)
      return null;

    const document = new DOMParser().parseFromString(value, "text/xml");

    if (document.childNodes.length == 1 && (document.firstChild as HTMLElement).tagName == this.tagName) {
      return null;
    }

    return {
      invalidContent: true
    };
  }

  writeValue(value: string): void {
    this.ckEditorComponent.writeValue(value);
  }

  onReady($event: CKEditor4.EventInfo) {
    $event.editor.on("afterInsertHtml", () => {
      this._afterInsertHtmlSubject.next($event.editor);
    })
  }

  registerOnChange(fn: any): void {
    merge(this.ckEditorComponent.change)
      .pipe(
        takeUntil(this._destroyed$),
        map(($event: CKEditor4.EventInfo) => $event.editor.getData()),
        switchMap(html => this._afterInsertHtml$.pipe(
          map(e => e.getData()),
          startWith(html)
        ))
      )
      .subscribe(fn);
  }

  config = {
    removeDialogTabs: 'image:advanced;image:Link;link:advanced;link:upload',
    filebrowserImageUploadUrl: `${environment.apiURL}/administration/imageupload?single=true`,
    extraPlugins: 'colorbutton,colordialog,preview,a11ychecker,wysiwygarea,dialog,docprops,justify,placeholder',
    language: this._translateService.currentLang,
    allowedContent: true,
    fullPage: true
  }
}

@NgModule({
  declarations: [
    HtmlEditorComponent
  ],
  exports: [
    HtmlEditorComponent
  ],
  imports: [
    CommonModule,
    CKEditorModule,
    ReactiveFormsModule,
    TranslateModule
  ]
})
export class HtmlEditorModule { }
